import { onMounted, onUnmounted, watch } from 'vue';

import { CHAT_ALERT_EVENT } from '@/shared/composables/useChatSoundNotifications';
import { useWindowAttention } from '@/shared/composables/useWindowAttention';

import type { Ref } from 'vue';

interface BadgingNavigator {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

const badging = navigator as BadgingNavigator;

const BADGE_PREFIX_RE = /^\(\d+\)\s/;

const BLINK_TITLE = '● Нове повідомлення';
const BLINK_INTERVAL_MS = 1_000;

const stripPrefix = (title: string) => title.replace(BADGE_PREFIX_RE, '');

// Badging API rejections are expected outside an installed PWA — swallow them.
const ignoreRejection = () => undefined;

/**
 * Mirrors the unread counter onto the installed-PWA taskbar icon via the
 * Badging API and onto `document.title` as a `(N)` prefix for the plain-tab
 * fallback. Degrades silently where the Badging API is unavailable. The title
 * base is read live (prefix-stripped) so per-route title changes survive.
 *
 * Поверх лічильника — блимання заголовка. Число `(N)` у вкладці змінюється
 * тихо, і за іншою програмою його не видно зовсім; тому коли приходить нове
 * повідомлення чи ескалація, а секретар на вікно не дивиться
 * (`CHAT_ALERT_EVENT`), заголовок раз на секунду стає «● Нове повідомлення» —
 * так само, як це роблять пошта й месенджери. Блимання зупиняється, щойно
 * вікно отримує фокус або людина повертається до нього.
 *
 * Базовий заголовок під час блимання тримається окремо (`baseTitle`): у фазі
 * «● …» у `document.title` його просто немає, і читати його звідти не можна.
 */
export const useAppBadge = (count: Ref<number>) => {
  const { isAttending, isFocused } = useWindowAttention();

  let currentCount = 0;
  let baseTitle = stripPrefix(document.title);
  let titleObserver: MutationObserver | null = null;
  let blinkTimer: ReturnType<typeof setInterval> | null = null;
  let blinkOn = false;

  const normalTitle = () => (currentCount > 0 ? `(${currentCount}) ${baseTitle}` : baseTitle);

  const apply = (value: number) => {
    currentCount = value;

    if (value > 0) {
      badging.setAppBadge?.(value)?.catch(ignoreRejection);
    } else {
      badging.clearAppBadge?.()?.catch(ignoreRejection);
    }

    // У фазі «● …» заголовок не чіпаємо — наступний тік сам покаже новий лічильник.
    if (!blinkOn) {
      document.title = normalTitle();
    }
  };

  const stopBlink = () => {
    if (blinkTimer !== null) {
      clearInterval(blinkTimer);
      blinkTimer = null;
    }
    if (blinkOn) {
      blinkOn = false;
      document.title = normalTitle();
    }
  };

  const startBlink = () => {
    if (blinkTimer !== null || isAttending.value) {
      return;
    }

    blinkTimer = setInterval(() => {
      blinkOn = !blinkOn;
      document.title = blinkOn ? BLINK_TITLE : normalTitle();
    }, BLINK_INTERVAL_MS);
  };

  watch(count, apply, { immediate: true });

  watch([isAttending, isFocused], ([attending, focusedNow], [, focusedBefore]) => {
    if (attending || (focusedNow && !focusedBefore)) {
      stopBlink();
    }
  });

  onMounted(() => {
    const title = document.querySelector('title') ?? document.head.appendChild(document.createElement('title'));

    window.addEventListener(CHAT_ALERT_EVENT, startBlink);

    // Сторінка всередині макета ставить свій заголовок (`useTitle`) у власному
    // setup — після нашого першого `apply`, але до цього спостерігача. Без
    // звірки лічильник далі дописувався б до заголовка, якого вже немає.
    if (!blinkOn && document.title !== BLINK_TITLE) {
      baseTitle = stripPrefix(document.title);
      apply(currentCount);
    }

    titleObserver = new MutationObserver(() => {
      const current = document.title;

      // Наш власний кадр блимання — не новий заголовок сторінки.
      if (current === BLINK_TITLE) {
        return;
      }

      baseTitle = stripPrefix(current);

      if (current === normalTitle() || blinkOn) {
        return;
      }

      apply(currentCount);
    });

    titleObserver.observe(title, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  });

  onUnmounted(() => {
    window.removeEventListener(CHAT_ALERT_EVENT, startBlink);
    titleObserver?.disconnect();
    stopBlink();
    badging.clearAppBadge?.()?.catch(ignoreRejection);
    document.title = baseTitle;
  });
};
